import React, { useEffect, useState } from 'react';
import { Mail, Calendar, CheckCircle, XCircle, AlertCircle, RefreshCw, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';

const AdminLoginAttempts = () => {
  const [attempts, setAttempts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, success, failed

  useEffect(() => {
    fetchAttempts();
  }, [filter]);

  const fetchAttempts = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (filter === 'success') params.success = true;
      if (filter === 'failed') params.success = false;

      const response = await adminAPI.getLoginAttempts(params);
      setAttempts(response.data.attempts || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching login attempts:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBrowserInfo = (userAgent) => {
    if (!userAgent) return 'Inconnu';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Autre';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
            Tentatives de connexion ({total})
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchAttempts}>
            <RefreshCw size={16} />
          </Button>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les tentatives</SelectItem>
            <SelectItem value="failed">Echecs uniquement</SelectItem>
            <SelectItem value="success">Reussies uniquement</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Attempts list */}
      {attempts.length === 0 ? (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <Mail size={48} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500">Aucune tentative de connexion</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {attempts.map((attempt) => (
            <div
              key={attempt.id}
              className={`p-4 rounded-lg border ${
                attempt.success
                  ? 'bg-green-50 border-green-200'
                  : attempt.attempted
                  ? 'bg-red-50 border-red-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  {/* Email */}
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-gray-500" />
                    <span className="font-medium">{attempt.email}</span>
                  </div>

                  {/* Error message if failed */}
                  {attempt.attempted && !attempt.success && attempt.error_message && (
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle size={14} />
                      <span className="text-sm">{attempt.error_message}</span>
                    </div>
                  )}

                  {/* Browser info */}
                  {attempt.user_agent && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Monitor size={14} />
                      {getBrowserInfo(attempt.user_agent)}
                    </div>
                  )}

                  {/* Date */}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar size={14} />
                    {formatDate(attempt.updated_at)}
                  </div>
                </div>

                {/* Status badge */}
                <div className="flex items-center gap-1">
                  {attempt.success ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle size={12} />
                      Reussi
                    </span>
                  ) : attempt.attempted ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <XCircle size={12} />
                      Echec
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <AlertCircle size={12} />
                      Non soumis
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminLoginAttempts;
